import { Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthLegacyService } from '@App/auth/auth-legacy.service';
import { IdleService, NotificationService, NotificationSeverity } from '@Services';
import { CreateMyNoteRequest, UWBaseProps } from '@Underwriting/models';
import { UWWorkbenchService } from '@Underwriting/services';
import UWNotesFeatureToggleDataService from 'app/case-summary/underwriting/services/uw-notes-feature-toggle/uw-notes-feature-toggle.data.service';
import { NoteTypes } from 'app/enums';
import { SubSink } from 'subsink';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { NgIf } from '@angular/common';
import { MatFormField, MatLabel, MatPrefix, MatError } from '@angular/material/form-field';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { SharedModule } from '@Shared/shared.module';
import { RichTextEditorComponent } from '@Shared/components/rich-text-editor/rich-text-editor.component';
import SharedAddNoteFormComponent from '@CaseSummary/underwriting/components/shared-add-note-form/shared-add-note-form.component';

export interface UWNotesAddFormProps {
    dropShadow?: boolean;
    caseRequirementName?: string;
    caseRequirementSubtitle?: string;
    caseRequirementId?: string;
    integrationId?: string;
    integrationActionId?: string;
    noteType?: NoteTypes
}

@Component({
    selector: 'uw-notes-add-form',
    templateUrl: './uw-notes-add-form.component.html',
    styleUrls: ['./uw-notes-add-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, RxReactiveFormsModule, NgIf, MatFormField, MatLabel, MatIconButton, 
        MatPrefix, MatTooltip, MatIcon, MatInput, CdkTextareaAutosize, MatError, MatButton, SharedModule, RichTextEditorComponent, SharedAddNoteFormComponent]
})
export default class UWNotesAddForm implements OnInit, OnDestroy, OnChanges {
    @Input() baseProps: UWBaseProps;
    @Input() props: UWNotesAddFormProps;
    @Input() disabled: boolean = false;

    noteForm: UntypedFormGroup;
    noteError = false;
    isHiddenNoteFeatureEnabled = false;
    isHiddenNoteAccountSettingEnabled = false;
    updatedRequirementsTableDesign = false;

    subs = new SubSink();

    constructor(
        public _fb: UntypedFormBuilder,
        private underWriterWorkbenchService: UWWorkbenchService,
        private notificationService: NotificationService,
        private uwNotesFeatureToggle: UWNotesFeatureToggleDataService,
        private idleService: IdleService,
        private authService: AuthLegacyService       
    ) {
        this.noteForm = this._fb.group({
            isInternalNote: [false, Validators.required],
            note: ['', Validators.required]
        });
    }

    //#region Helpers
    
    enableNoteError = () => this.noteError = true;
    disableNoteError = () => this.noteError = false;

    createMyNoteRequest(note: string, isInternalNote: boolean): CreateMyNoteRequest {
    
        return {
            caseId: this.baseProps.case.id,
            message: note,
            isInternalNote: isInternalNote,
            caseRequirement: {
                caseRequirementName: this.props.caseRequirementName,
                caseRequirementSubtitle: this.props.caseRequirementSubtitle,
                caseRequirementId: this.props.caseRequirementId,
                integrationId: this.props.integrationId,
                integrationActionId: this.props.integrationActionId,
            },
            noteTypeId: this.props.noteType || NoteTypes.Standard
        };
    }

    //#endregion

    //#region Handlers

    clearNote() {
        this.noteError = false;
        this.noteForm.reset();
        this.noteForm.get('note').setValue('');
        this.noteForm.get('note').setErrors(null);
        this.noteForm.get('isInternalNote').setValue(false);
    }

    addNote(isAutoSave: boolean = false) {
        this.noteForm.get('note').updateValueAndValidity();
        const note: string = this.noteForm.get('note').value;
        const isInternalNote = this.noteForm.get('isInternalNote').value;

        if (!note || note.length == 0) {
            this.notificationService.showNotification({
                severity: NotificationSeverity.Info,
                message: 'Please enter a valid note.'
            });
            
            if (isAutoSave) {
                this.authService.logout();
                return;
            }

            return this.enableNoteError();
        }
        
        const request = this.createMyNoteRequest(note, isInternalNote);

        this.disableNoteError();
        this.subs.add(this.underWriterWorkbenchService.addMyNote(this.baseProps.account.id, request).subscribe(() => {
            this.notificationService.showNotification({ severity: NotificationSeverity.Success, message: "Added note successfully..." });
            this.clearNote();
            this.idleService.areUwwNotesInAddMode = false;
            if (isAutoSave) {
                this.authService.logout();
            }
        }));
    }

    toggleNoteVisibility() {
        const isInternalNoteValue: boolean = this.noteForm.get('isInternalNote').value;

        if (isInternalNoteValue)
            this.noteForm.patchValue({'isInternalNote': false}, {onlySelf: true, emitEvent: false});
        else
            this.noteForm.patchValue({'isInternalNote': true}, {onlySelf: true, emitEvent: false});
    }
    //#endregion

    //#region Lifecycle

    ngOnInit() {
        this.subs.add(this.uwNotesFeatureToggle.loadHiddenNotesFeatureSetting(this.baseProps.account).subscribe((hiddenNoteFeatureSetting) => {
            this.isHiddenNoteFeatureEnabled = hiddenNoteFeatureSetting.featureToggle;
            this.isHiddenNoteAccountSettingEnabled = hiddenNoteFeatureSetting.accountSetting;
            this.updatedRequirementsTableDesign = hiddenNoteFeatureSetting.updatedRequirementsTableDesign;
        }));

        const noteContentChangeSubscription = this.noteForm.get('note').valueChanges.subscribe(value => {
            const isAddingNote = !!value?.trim();
            this.idleService.areUwwNotesInAddMode = isAddingNote;
        });
        this.subs.add(noteContentChangeSubscription);

        const idleSubscription = this.idleService.triggeredSignoutAutoSaveUwwAddNote.subscribe(async _data => {
            await this.addNote(true);
        });
        this.subs.add(idleSubscription);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['disabled']) {
            if (this.disabled) {
                this.noteForm.get('note').disable({ emitEvent: false });
                this.noteForm.get('isInternalNote').disable({ emitEvent: false });
            } else {
                this.noteForm.get('note').enable({ emitEvent: false });
                this.noteForm.get('isInternalNote').enable({ emitEvent: false });
            }
        }
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.idleService.areUwwNotesInAddMode = false;
    }

    //#endregion
}
